/** @jsx jsx */
import React, { useState, useContext } from "react";
import { css, jsx, keyframes } from "@emotion/react";
import { 
  Box, 
  Fab, 
  Drawer, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  IconButton, 
  Divider,
  Paper,
  Tooltip,
  useTheme
} from "@mui/material";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BlockIcon from '@mui/icons-material/Block';
import { ThemeContext } from "context/ThemeContext";
import { AuthContext } from "context/Authentication";
import Header from "../../components/Header/Header.jsx";
import Footer from "templates/default/components/Footer/Footer.jsx";
import PageRenderer from "../../../../PageRenderer/PageRenderer";

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export default function Home(props) {
  const context = useContext(AuthContext);
  const themeMode = useContext(ThemeContext);
  const theme = useTheme(); // Use MUI theme
  const darkMode = themeMode.state.darkMode;
  const [headerRefresh, setHeaderRefresh] = useState(true);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  // Define color pulse animation using theme colors
  const colorPulse = keyframes`
    0% {
      background-color: ${theme.palette.primary.main};
    }
    50% {
      background-color: ${theme.palette.secondary.main};
    }
    100% {
      background-color: ${theme.palette.primary.main};
    }
  `;

  // Parse Delivery Schedule
  const scheduleSetting = context.state?.appSettings?.find(s => s.Setting_Name === 'Delivery Schedule');
  const hasSchedule = scheduleSetting && scheduleSetting.Setting_Value;

  const getGroupedSchedule = () => {
    if (!hasSchedule) return [];
    try {
      const pairs = scheduleSetting.Setting_Value.split(',').map(pair => {
        const parts = pair.split('-');
        if (parts.length === 2) {
          return { 
            orderDay: parts[0].trim(), 
            deliveryDay: parts[1].trim()
          };
        }
        return null;
      }).filter(item => item !== null);

      // Group by Delivery Day
      const groups = [];
      pairs.forEach(pair => {
         const existing = groups.find(g => g.deliveryDay === pair.deliveryDay);
         if (existing) {
             existing.orderDays.push(pair.orderDay);
         } else {
             groups.push({
                 deliveryDay: pair.deliveryDay,
                 orderDays: [pair.orderDay]
             });
         }
      });
      return groups;
    } catch (e) {
      return [];
    }
  };

  const scheduleItems = getGroupedSchedule();

  // Mapping day short codes to full names for better display
  const dayMap = {
    'Mon': 'Monday',
    'Tue': 'Tuesday',
    'Wed': 'Wednesday',
    'Thu': 'Thursday',
    'Fri': 'Friday',
    'Sat': 'Saturday',
    'Sun': 'Sunday'
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          width: "100%",
        }}
      >
        <Header
          headerRefresh={headerRefresh}
          setHeaderRefresh={setHeaderRefresh}
        />

        {/* All page content (banners and sections) managed by PageRenderer */}
        <PageRenderer
          pageId={37}
          headerRefresh={headerRefresh}
          setHeaderRefresh={setHeaderRefresh}
        />

        <Footer />

        {/* Floating Delivery Schedule Button */}
        {hasSchedule && (
            <Paper 
              elevation={4}
              onClick={() => setScheduleOpen(true)}
              sx={{
                position: 'fixed',
                top: '20%',
                right: 0,
                zIndex: 1200,
                // bgcolor initial state handled by animation, but setting specific here for fallback
                bgcolor: 'primary.main',
                color: '#fff',
                borderTopLeftRadius: 50,
                borderBottomLeftRadius: 50,
                cursor: 'pointer',
                pl: 2.5,
                pr: 2,
                py: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                // Use 'backwards' for slideIn so it releases control of transform after finishing, allowing hover to work
                animation: `${slideInRight} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) backwards, ${colorPulse} 8s infinite ease-in-out`,
                transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease', 
                '&:hover': {
                  transform: 'translateX(-8px) scale(1.02)',
                  boxShadow: 6,
                }
              }}
            >
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventNoteIcon color="inherit" />
                  <Typography variant="body2" fontWeight="bold" color="inherit">Delivery Schedule</Typography>
               </Box>
               <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', mt: 0.2, lineHeight: 1 }}>
                 Click Here
               </Typography>
            </Paper>
        )}

        {/* Delivery Schedule Sliding Card/Drawer */}
        <Drawer
          anchor="right"
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: '85%', sm: 320 },
              height: 'auto',
              maxHeight: '80vh',
              top: '20% !important',
              right: '20px !important',
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
            }
          }}
          // Enhance modal presentation
          ModalProps={{
            keepMounted: true, 
            BackdropProps: { sx: { backgroundColor: 'rgba(0,0,0,0.2)' } } 
          }}
        >
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShippingIcon fontSize="small" />
                <Typography variant="subtitle1" fontWeight="bold">Delivery Schedule</Typography>
              </Box>
              <IconButton onClick={() => setScheduleOpen(false)} size="small" sx={{ color: 'white' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.9, display: 'block', lineHeight: 1.2 }}>
              Check delivery days based on order time.
            </Typography>
          </Box>

          <List sx={{ pt: 1, pb: 1, maxHeight: '60vh', overflowY: 'auto' }}>
            {scheduleItems.map((group, index) => {
              const isBlocked = group.deliveryDay.toLowerCase() === 'no';
              const orderDaysString = group.orderDays.map(d => dayMap[d] || d).join(', ');
              
              return (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start" sx={{ px: 2, py: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 32, mt: 0.2 }}>
                      <CalendarMonthIcon color="action" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="600" color="text.primary" sx={{ lineHeight: 1.3 }}>
                          Order: {orderDaysString}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
                          {isBlocked ? (
                            <>
                              <BlockIcon fontSize="inherit" color="error" sx={{ fontSize: 16 }} />
                              <Typography variant="caption" color="error.main" fontWeight="500">
                                No Delivery
                              </Typography>
                            </>
                          ) : (
                            <>
                              <LocalShippingIcon fontSize="inherit" color="success" sx={{ fontSize: 16 }} />
                              <Typography variant="caption" color="success.main" fontWeight="600">
                                Delivers: {dayMap[group.deliveryDay] || group.deliveryDay}
                              </Typography>
                            </>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < scheduleItems.length - 1 && <Divider component="li" variant="inset" sx={{ ml: 6 }} />}
                </React.Fragment>
              );
            })}
          </List>
          
          <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" display="block" align="center" sx={{ fontSize: '0.65rem' }}>
              * Dates are estimates & subject to availability.
            </Typography>
          </Box>
        </Drawer>

      </Box>
    </React.Fragment>
  );
}
